import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, vModelSelect as _vModelSelect, withDirectives as _withDirectives, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "assessment-list-container" }
const _hoisted_2 = { class: "top-bar" }
const _hoisted_3 = { class: "top-bar-content" }
const _hoisted_4 = {
  key: 0,
  class: "success-notice"
}
const _hoisted_5 = { class: "content-container" }
const _hoisted_6 = {
  key: 0,
  class: "loading-state"
}
const _hoisted_7 = {
  key: 1,
  class: "error-state"
}
const _hoisted_8 = {
  key: 2,
  class: "empty-state"
}
const _hoisted_9 = { key: 3 }
const _hoisted_10 = { class: "controls-bar" }
const _hoisted_11 = { class: "filter-group" }
const _hoisted_12 = { class: "filter-buttons" }
const _hoisted_13 = { class: "sort-group" }
const _hoisted_14 = {
  key: 0,
  class: "empty-state"
}
const _hoisted_15 = {
  key: 1,
  class: "assessments-grid"
}
const _hoisted_16 = ["onClick"]
const _hoisted_17 = { class: "assessment-header" }
const _hoisted_18 = { class: "assessment-title" }
const _hoisted_19 = { class: "assessment-date" }
const _hoisted_20 = { class: "assessment-details" }
const _hoisted_21 = { class: "detail-row" }
const _hoisted_22 = { class: "value" }
const _hoisted_23 = { class: "detail-row" }
const _hoisted_24 = { class: "value" }
const _hoisted_25 = { class: "detail-row" }
const _hoisted_26 = { class: "value" }
const _hoisted_27 = {
  key: 0,
  class: "detail-row"
}
const _hoisted_28 = { class: "value" }
const _hoisted_29 = { class: "assessment-actions" }
const _hoisted_30 = { class: "grade-button" }

import UserSection from "@/components/UserSection.vue";
import { useAuthStore } from "@/stores";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

interface AssessmentSummary {
  id: string;
  email: string;
  userName: string;
  submittedTimestamp: number;
  course?: {
    id: string;
    title: string;
  };
  section?: {
    id: string;
    title: string;
  };
  learningUnit?: {
    id: string;
    title: string;
    type: string;
  };
  answers?: Array<{
    blockId: string;
    blockType: string;
  }>;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AssessmentList',
  setup(__props) {

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

const assessments = ref<AssessmentSummary[]>([]);
const loading = ref(true);
const error = ref<string | null>(null);

// Filter and sort state
const currentFilter = ref<"tehtavat" | "pikatehtavat" | "all">("tehtavat");
const currentSort = ref<"oldest" | "newest" | "course" | "learning_unit">(
  "oldest",
);

// Success notice state
const showSuccessNotice = ref(false);

// Computed properties for filtering and sorting
const tehtavatAssessments = computed(() => {
  return assessments.value.filter(
    (assessment) => !assessment.learningUnit?.title?.startsWith("Pikatehtävä"),
  );
});

const pikatehtavatAssessments = computed(() => {
  return assessments.value.filter((assessment) =>
    assessment.learningUnit?.title?.startsWith("Pikatehtävä"),
  );
});

const tehtavatCount = computed(() => tehtavatAssessments.value.length);
const pikatehtavatCount = computed(() => pikatehtavatAssessments.value.length);

const currentFilterText = computed(() => {
  switch (currentFilter.value) {
    case "tehtavat":
      return "tehtäviä";
    case "pikatehtavat":
      return "pikatehtäviä";
    case "all":
      return "töitä";
    default:
      return "töitä";
  }
});

const filteredAssessments = computed(() => {
  switch (currentFilter.value) {
    case "tehtavat":
      return tehtavatAssessments.value;
    case "pikatehtavat":
      return pikatehtavatAssessments.value;
    case "all":
      return assessments.value;
    default:
      return tehtavatAssessments.value;
  }
});

const filteredAndSortedAssessments = computed(() => {
  const filtered = [...filteredAssessments.value];

  switch (currentSort.value) {
    case "oldest":
      return filtered.sort(
        (a, b) => a.submittedTimestamp - b.submittedTimestamp,
      );
    case "newest":
      return filtered.sort(
        (a, b) => b.submittedTimestamp - a.submittedTimestamp,
      );
    case "course":
      return filtered.sort((a, b) => {
        const courseA = a.course?.title || "";
        const courseB = b.course?.title || "";
        return courseA.localeCompare(courseB);
      });
    case "learning_unit":
      return filtered.sort((a, b) => {
        const titleA = a.learningUnit?.title || "";
        const titleB = b.learningUnit?.title || "";
        return titleA.localeCompare(titleB);
      });
    default:
      return filtered.sort(
        (a, b) => a.submittedTimestamp - b.submittedTimestamp,
      );
  }
});

// Filter functions
const setFilter = (filter: "tehtavat" | "pikatehtavat" | "all") => {
  currentFilter.value = filter;
};

// Assessment functions
const fetchAssessments = async () => {
  loading.value = true;
  error.value = null;

  try {
    const apiBaseUrl =
      (process.env.VUE_APP_API_BASE_URL as string) ||
      "http://localhost:3000/api";
    const response = await fetch(`${apiBaseUrl}/assessment_responses`, {
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Failed to fetch assessments");
    }

    const data = (await response.json()) as {
      assessments: AssessmentSummary[];
    };
    assessments.value = data.assessments || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : "Unknown error";
    console.error("Error fetching assessments:", err);
  } finally {
    loading.value = false;
  }
};

const openAssessment = async (assessmentId: string) => {
  await router.push(`/grading/${assessmentId}`);
};

const formatDate = (timestamp: number): string => {
  const date = new Date(timestamp * 1000); // Convert from seconds to milliseconds
  return date.toLocaleDateString("fi-FI", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Reactive state to track which assessments have saved state
const assessmentsWithState = ref<Set<string>>(new Set());

// Helper function to check if assessment has saved state (using reactive state)
const hasAssessmentState = (assessmentId: string): boolean => {
  return assessmentsWithState.value.has(assessmentId);
};

// Get button text based on whether assessment has saved state
const getButtonText = (assessmentId: string): string => {
  return hasAssessmentState(assessmentId)
    ? "Jatka arviointia"
    : "Aloita arviointi";
};

// Load assessment states from Pinia (with background refresh)
const loadAssessmentStates = async (forceRefresh = false) => {
  if (!authStore.currentProfile) {
    assessmentsWithState.value = new Set();
    return;
  }

  try {
    // Load immediately from cache for instant rendering
    const cachedStates = authStore.getCurrentProfileAssessmentList;
    if (cachedStates.length > 0 && !forceRefresh) {
      const assessmentIds = cachedStates.map((state) => state.assessment_id);
      assessmentsWithState.value = new Set(assessmentIds);
      console.log(
        `📊 Loaded ${assessmentIds.length} assessment states from cache`,
      );
    }

    // Load from API and update cache (background refresh)
    const apiStates = await authStore.loadAssessmentList(forceRefresh);
    const assessmentIds = apiStates.map((state) => state.assessment_id);
    assessmentsWithState.value = new Set(assessmentIds);

    console.log(`📊 Loaded ${assessmentIds.length} assessment states from API`);
  } catch (err) {
    console.warn("Failed to load assessment states:", err);
    // Fallback to empty set
    assessmentsWithState.value = new Set();
  }
};

// Watch for success query parameter
watch(
  () => route.query.submitted as string | undefined,
  (submitted: string | undefined) => {
    if (submitted === "true") {
      showSuccessNotice.value = true;

      // Auto-close after 4 seconds
      setTimeout(() => {
        showSuccessNotice.value = false;
      }, 4000);

      // Clear the query parameter
      void router.replace({ path: "/assessments" });
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await fetchAssessments();
  await loadAssessmentStates();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[4] || (_cache[4] = _createElementVNode("div", { class: "logo-section" }, [
          _createElementVNode("img", {
            src: "/Teho_aly_logo.svg",
            alt: "Teho-aly",
            class: "logo",
            onerror: "this.src='/Teho_aly_logo.png'"
          })
        ], -1)),
        _cache[5] || (_cache[5] = _createElementVNode("h1", { class: "page-title" }, "Arvioitavat työt", -1)),
        _createVNode(UserSection)
      ])
    ]),
    (showSuccessNotice.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[6] || (_cache[6] = [
          _createElementVNode("div", { class: "success-content" }, [
            _createElementVNode("span", { class: "success-icon" }, "✓"),
            _createElementVNode("span", { class: "success-text" }, "Arviointi lähetetty onnistuneesti")
          ], -1)
        ]))]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [...(_cache[7] || (_cache[7] = [
            _createElementVNode("div", { class: "loading-spinner" }, null, -1),
            _createElementVNode("p", null, "Ladataan arvioitavia töitä...", -1)
          ]))]))
        : (error.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              _createElementVNode("p", null, "Virhe ladattaessa töitä: " + _toDisplayString(error.value), 1),
              _createElementVNode("button", {
                onClick: fetchAssessments,
                class: "retry-button"
              }, " Yritä uudelleen ")
            ]))
          : (assessments.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [...(_cache[8] || (_cache[8] = [
                _createElementVNode("h2", null, "Ei arvioitavia töitä", -1),
                _createElementVNode("p", null, "Kaikki työt on arvioitu tai ei ole uusia töitä saatavilla.", -1)
              ]))]))
            : (_openBlock(), _createElementBlock("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  _createElementVNode("div", _hoisted_11, [
                    _cache[9] || (_cache[9] = _createElementVNode("label", { class: "filter-label" }, "Näytä:", -1)),
                    _createElementVNode("div", _hoisted_12, [
                      _createElementVNode("button", {
                        class: _normalizeClass([
                  'filter-button',
                  { active: currentFilter.value === 'tehtavat' },
                ]),
                        onClick: _cache[0] || (_cache[0] = ($event: any) => (setFilter('tehtavat')))
                      }, " Tehtävät (" + _toDisplayString(tehtavatCount.value) + ") ", 3),
                      _createElementVNode("button", {
                        class: _normalizeClass([
                  'filter-button',
                  { active: currentFilter.value === 'pikatehtavat' },
                ]),
                        onClick: _cache[1] || (_cache[1] = ($event: any) => (setFilter('pikatehtavat')))
                      }, " Pikatehtävät (" + _toDisplayString(pikatehtavatCount.value) + ") ", 3),
                      _createElementVNode("button", {
                        class: _normalizeClass(['filter-button', { active: currentFilter.value === 'all' }]),
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (setFilter('all')))
                      }, " Kaikki (" + _toDisplayString(assessments.value.length) + ") ", 3)
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_13, [
                    _cache[11] || (_cache[11] = _createElementVNode("label", { class: "sort-label" }, "Järjestys:", -1)),
                    _withDirectives(_createElementVNode("select", {
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((currentSort).value = $event)),
                      class: "sort-select"
                    }, [...(_cache[10] || (_cache[10] = [
                      _createElementVNode("option", { value: "oldest" }, "Vanhin ensin", -1),
                      _createElementVNode("option", { value: "newest" }, "Uusin ensin", -1),
                      _createElementVNode("option", { value: "course" }, "Kurssin nimi", -1),
                      _createElementVNode("option", { value: "learning_unit" }, "Tehtävän nimi", -1)
                    ]))], 512), [
                      [_vModelSelect, currentSort.value]
                    ])
                  ])
                ]),
                (filteredAndSortedAssessments.value.length === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                      _createElementVNode("h2", null, "Ei " + _toDisplayString(currentFilterText.value), 1),
                      _cache[12] || (_cache[12] = _createElementVNode("p", null, "Ei löytynyt yhtään työtä valituilla suodattimilla.", -1))
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_15, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredAndSortedAssessments.value, (assessment) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: assessment.id,
                          class: "assessment-card",
                          onClick: ($event: any) => (openAssessment(assessment.id))
                        }, [
                          _createElementVNode("div", _hoisted_17, [
                            _createElementVNode("h3", _hoisted_18, _toDisplayString(assessment.learningUnit?.title || "Arvioitava työ"), 1),
                            _createElementVNode("span", _hoisted_19, _toDisplayString(formatDate(assessment.submittedTimestamp)), 1)
                          ]),
                          _createElementVNode("div", _hoisted_20, [
                            _createElementVNode("div", _hoisted_21, [
                              _cache[13] || (_cache[13] = _createElementVNode("span", { class: "label" }, "Opiskelija:", -1)),
                              _createElementVNode("span", _hoisted_22, _toDisplayString(assessment.userName), 1)
                            ]),
                            _createElementVNode("div", _hoisted_23, [
                              _cache[14] || (_cache[14] = _createElementVNode("span", { class: "label" }, "Kurssi:", -1)),
                              _createElementVNode("span", _hoisted_24, _toDisplayString(assessment.course?.title), 1)
                            ]),
                            _createElementVNode("div", _hoisted_25, [
                              _cache[15] || (_cache[15] = _createElementVNode("span", { class: "label" }, "Osio:", -1)),
                              _createElementVNode("span", _hoisted_26, _toDisplayString(assessment.section?.title), 1)
                            ]),
                            (assessment.answers?.length)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_27, [
                                  _cache[16] || (_cache[16] = _createElementVNode("span", { class: "label" }, "Vastauksia:", -1)),
                                  _createElementVNode("span", _hoisted_28, _toDisplayString(assessment.answers.length) + " kpl", 1)
                                ]))
                              : _createCommentVNode("", true)
                          ]),
                          _createElementVNode("div", _hoisted_29, [
                            _createElementVNode("button", _hoisted_30, _toDisplayString(getButtonText(assessment.id)), 1)
                          ])
                        ], 8, _hoisted_16))
                      }), 128))
                    ]))
              ]))
    ])
  ]))
}
}

})